"use client";

import { Routes, Route, Navigate } from "react-router-dom";
import Layout from "./layouts/Layout";
import Dashboard from "./pages/Dashboard";
import Trades from "./pages/Trades";
import DailyReport from "./pages/Daily";
import WeeklyReport from "./pages/Weekly";
import MonthlyReport from "./pages/Monthly";
import TradeDashboard from "./pages/TradeDashboard";
import UsIntradayStocks from "./pages/UsIntradayStocks";
import UsTradeDashboard from "./pages/UsTradeDashboard";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import type {} from "@mui/x-data-grid/themeAugmentation";
import CssBaseline from "@mui/material/CssBaseline";
import AuthSuccess from "./pages/AuthSuccess";
import Login from "./pages/Login";
import UserManagement from "./pages/UserManagement";
import { LoginCallback } from "./pages/LoginCallback";
// import { AuthProvider } from "./contexts/AuthContext";
// import DashboardLayout from "./layouts/DashboardLayout";
import ProtectedRoute from "./layouts/ProtectedRoutes";
import OrdersPage from "./pages/Orders";
import PortfolioPage from "./pages/Portfolio";
import { NotificationsProvider } from "@toolpad/core/useNotifications";
import SwingStocks from "./pages/SwingStocks";
// Screener import removed
import WatchlistPage from "./pages/WatchlistPage";
import IntradayStocks from "./pages/IntradayStocks";
import OptionsDashboard from "./pages/options/OptionsDashboard";
import OptionChainPage from "./pages/options/OptionChainPage";
import StrategyRadarPage from "./pages/options/StrategyRadarPage";
import ActivePositionsPage from "./pages/options/ActivePositionsPage";
import RiskCockpitPage from "./pages/options/RiskCockpitPage";
import BacktestLabPage from "./pages/options/BacktestLabPage";
import DeepStatsPage from "./pages/options/DeepStatsPage";
import QualificationPage from "./pages/options/QualificationPage";
import ShadowModePage from "./pages/options/ShadowModePage";
import RealityComparisonPage from "./pages/options/RealityComparisonPage";
import OptionsHealthPage from "./pages/options/OptionsHealthPage";
import DataHealthPage from "./pages/options/DataHealthPage";
import StrategyVersionsPage from "./pages/options/StrategyVersionsPage";
import OptionsSettingsPage from "./pages/options/OptionsSettingsPage";
import EngineLogsPage from "./pages/options/EngineLogsPage";
import { useAuth } from "hooks/useAuth";
import LoadingSpinner from "components/LoadinSpinner";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const theme = createTheme({
  palette: {
    primary: {
      main: "#2A3F54", // Dark blue (sidebar)
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#1ABB9C", // Teal (accent)
    },
    success: {
      main: "#22c55e", // Green-500
      light: "#4ade80",
    },
    error: {
      main: "#ef4444", // Red-500
      light: "#f87171",
    },
    background: {
      default: "#F3F4F6", // Lighter gray for better contrast
      paper: "#ffffff",
    },
    text: {
      primary: "#111827", // Gray-900
      secondary: "#4B5563", // Gray-600
    },
  },
  typography: {
    fontFamily: "'Inter', sans-serif",
    h1: { fontWeight: 600 },
    h2: { fontWeight: 600 },
    h3: { fontWeight: 600 },
    h4: { fontWeight: 600 },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
    button: { textTransform: "none", fontWeight: 500 },
    // Custom variant for numbers (prices, percentages)
    subtitle2: {
      fontFamily: "'Roboto Mono', monospace",
      fontWeight: 500,
      fontSize: "0.80rem",
    },
  },
  components: {
    MuiDataGrid: {
      styleOverrides: {
        root: {
          backgroundColor: "#ffffff",
          "& .MuiDataGrid-cell": {
            borderBottom: "1px solid #f0f0f0",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "#f9fafb",
            color: "#374151",
            fontWeight: 600,
          },
        },
      },
    },
  },
});

function App() {
  const { isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner width="50px" height="50px" />;
  }

  // console.log("App.tsx: Rendering App component");
  return (
    <ThemeProvider theme={theme}>
      <NotificationsProvider>
        <CssBaseline /> {/* Normalize CSS */}
        <ToastContainer position="top-right" autoClose={3000} />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/callback" element={<LoginCallback />} />
          <Route path="/auth/success" element={<AuthSuccess />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route path="/" element={<TradeDashboard />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/user/manage" element={<UserManagement />} /> {/* Admin User Management */}
            <Route path="/swing-stocks" element={<SwingStocks />} />
            <Route path="/intraday-stocks" element={<IntradayStocks />} />
            <Route path="/trade-dashboard" element={<TradeDashboard />} />
            {/* <Route path="/us/intraday-stocks" element={<UsIntradayStocks />} /> */}
            {/* <Route path="/us/trade-dashboard" element={<UsTradeDashboard />} /> */}
// /scanner/screener route removed
            <Route path="/watchlist" element={<WatchlistPage />} />
            <Route path="/trades" element={<Trades />} />
            <Route path="/orders" element={<OrdersPage />} />
            <Route path="/portfolio" element={<PortfolioPage />} />
            {/* NIFTY Options Workspace Routes */}
            <Route path="/options" element={<OptionsDashboard />} />
            <Route path="/options/dashboard" element={<Navigate to="/options" replace />} />
            <Route path="/options/chain" element={<OptionChainPage />} />
            <Route path="/options/strategies" element={<StrategyRadarPage />} />
            <Route path="/options/positions" element={<ActivePositionsPage />} />
            <Route path="/options/risk" element={<RiskCockpitPage />} />
            <Route path="/options/backtest" element={<BacktestLabPage />} />
            <Route path="/options/stats" element={<DeepStatsPage />} />
            <Route path="/options/qualification" element={<QualificationPage />} />
            <Route path="/options/shadow" element={<ShadowModePage />} />
            <Route path="/options/reality" element={<RealityComparisonPage />} />
            <Route path="/options/health" element={<OptionsHealthPage />} />
            <Route path="/options/data-health" element={<DataHealthPage />} />
            <Route path="/options/versions" element={<StrategyVersionsPage />} />
            <Route path="/options/settings" element={<OptionsSettingsPage />} />
            <Route path="/options/logs" element={<EngineLogsPage />} />
            <Route path="/reports/daily" element={<DailyReport />} />
            <Route path="/reports/weekly" element={<WeeklyReport />} />
            <Route path="/reports/monthly" element={<MonthlyReport />} />
          </Route>
        </Routes>
      </NotificationsProvider>
    </ThemeProvider>
  );
}

export default App;
